import { FC } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { Box, Typography } from '@mui/material'

import { useUATaxonomy } from '../../hooks/useUATaxonomy'
import type { CreativeAd, StoryPattern, TermRef } from '../../types'
import { CreativeAdsTable } from '../CreativeAdsTable/CreativeAdsTable'
import { ListingTruncationNote } from '../ListingTruncationNote/ListingTruncationNote'
import { SectionCard } from '../SectionCard/SectionCard'
import { ShowcaseDetailHeader, ShowcaseDetailStat } from '../ShowcaseDetailHeader/ShowcaseDetailHeader'
import { StoryPatternsTable } from '../StoryPatternsTable/StoryPatternsTable'
import { TableSkeleton } from '../TableSkeleton/TableSkeleton'

interface MotivationDetailLayoutProps {
  /** Display title for the hero header (already formatted by the caller). */
  title: string
  /** Resolved to a localised label for the description copy. */
  motivation: TermRef
  /** Motivation accent colour used for the header dot. */
  color: string
  stats: ShowcaseDetailStat[]
  /** Story patterns leaning on this motivation. */
  patterns: StoryPattern[]
  /** True while the patterns are still loading (renders a skeleton). */
  isLoadingPatterns?: boolean
  /** Hide the patterns table's "Top Games" column (game-scoped views). */
  hideTopGames?: boolean
  onSelectPattern: (patternId: string) => void
  creatives: CreativeAd[]
  /** Creatives matching before paging; shows a truncation note when more than `creatives`. */
  creativesTotal?: number
}

/**
 * Shared layout for the Motivation detail views (dashboard and game page):
 * hero header with stats, the story patterns that lean on the motivation, and
 * the motivation's top creatives. Callers own data fetching and navigation;
 * this component only renders.
 */
export const MotivationDetailLayout: FC<MotivationDetailLayoutProps> = ({
  title,
  motivation,
  color,
  stats,
  patterns,
  isLoadingPatterns = false,
  hideTopGames = false,
  onSelectPattern,
  creatives,
  creativesTotal,
}) => {
  const { t } = useTranslation('ua-insights')
  const { motivationLabel } = useUATaxonomy()

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <ShowcaseDetailHeader
        accent="motivation"
        title={title}
        badgeLabel={t('showcase_badge_motivation')}
        dotColor={color}
        desc={
          <Trans
            i18nKey="ua-insights:showcase_motivation_detail_desc"
            values={{ motivation: motivationLabel(motivation) }}
            components={{ strong: <strong /> }}
          />
        }
        stats={stats}
      />

      <SectionCard title={t('showcase_patterns_using_motivation', { count: patterns.length })} disableBodyPadding={patterns.length > 0}>
        {isLoadingPatterns ? (
          <TableSkeleton rows={5} columns={4} />
        ) : patterns.length ? (
          <StoryPatternsTable
            patterns={patterns}
            firstColumnLabel={t('showcase_col_story_pattern')}
            onSelectPattern={onSelectPattern}
            hideTopGames={hideTopGames}
            disableCard
          />
        ) : (
          <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>{t('showcase_no_patterns_for_motivation')}</Typography>
        )}
      </SectionCard>

      <CreativeAdsTable rows={creatives} />
      {creativesTotal !== undefined && <ListingTruncationNote shown={creatives.length} total={creativesTotal} />}
    </Box>
  )
}
